import type { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import { useQueryClient, useMutation, InfiniteData } from 'react-query';
import { VoicemailApi } from '@frontend/api-voicemails';
import { useAppScopeStore } from '@frontend/scope';
import type { VoicemailQueryData, VoicemailTableDataRow } from '../components/all-calls/types';

// Add the tag to a voicemail before even the request is made
// assuming it will succeed. If it fails, we will revert the change
export const optimisticAddVoicemailTag = ({
  oldData,
  tag,
  voicemailId,
}: {
  oldData?: InfiniteData<VoicemailQueryData>;
  tag: Tag;
  voicemailId: string;
}): InfiniteData<VoicemailQueryData> | undefined => {
  if (oldData) {
    return {
      pageParams: oldData.pageParams,
      pages: oldData.pages.map((page) => {
        return {
          ...page,
          rows: page.rows.map((row) => {
            if (row.voicemailId === voicemailId) {
              return {
                ...row,
                tags: [...row.tags, tag],
                tagIds: [...row.tagIds, tag.id],
              };
            }
            return row;
          }),
        };
      }),
    };
  }

  return undefined;
};

export const optimisticRemoveVoicemailTag = ({
  oldData,
  tag,
  voicemailId,
}: {
  oldData?: InfiniteData<VoicemailQueryData>;
  tag: Tag;
  voicemailId: string;
}): InfiniteData<VoicemailQueryData> | undefined => {
  if (oldData) {
    return {
      pageParams: oldData.pageParams,
      pages: oldData.pages.map((page) => {
        return {
          ...page,
          rows: page.rows.map((row) => {
            if (row.voicemailId === voicemailId) {
              return {
                ...row,
                tags: row.tags.filter((t) => t.id !== tag.id),
                tagIds: row.tagIds.filter((id) => id !== tag.id),
              };
            }
            return row;
          }),
        };
      }),
    };
  }

  return undefined;
};

export const useVoicemailTagMutation = ({
  queryKey,
  voicemailId,
  optimisticAddTag,
  optimisticRemoveTag,
  onSuccess,
  onFailure,
}: {
  queryKey: string[];
  voicemailId: string;
  optimisticAddTag?: (args: {
    oldData: any | undefined;
    tag: Tag;
    voicemailId: string;
  }) => InfiniteData<VoicemailQueryData> | VoicemailTableDataRow | undefined;
  optimisticRemoveTag?: (args: {
    oldData: any | undefined;
    tag: Tag;
    voicemailId: string;
  }) => InfiniteData<VoicemailQueryData> | VoicemailTableDataRow | undefined;
  onSuccess?: (args: {
    tag: Tag;
    op: 'remove' | 'add';
    newData: InfiniteData<VoicemailQueryData> | VoicemailTableDataRow | undefined;
  }) => void;
  onFailure?: (args: {
    tag: Tag;
    op: 'remove' | 'add';
    oldData: InfiniteData<VoicemailQueryData> | VoicemailTableDataRow | undefined;
  }) => void;
}) => {
  const { selectedLocationIds } = useAppScopeStore();

  const qc = useQueryClient();

  const { mutate: addTag } = useMutation({
    mutationFn: async (tag: Tag) => {
      return VoicemailApi.tagVoicemail({ voicemailMessageId: voicemailId, tagIds: [tag.id] });
    },
    onMutate: async (tag: Tag) => {
      if (optimisticAddTag) {
        const oldData = qc.getQueryData<InfiniteData<VoicemailQueryData> | VoicemailTableDataRow | undefined>([
          selectedLocationIds,
          ...queryKey,
        ]);
        const newData = optimisticAddTag({ oldData, tag, voicemailId });
        if (newData) {
          qc.setQueryData<InfiniteData<VoicemailQueryData> | VoicemailTableDataRow | undefined>(
            [selectedLocationIds, ...queryKey],
            newData
          );
          return { oldData, newData };
        }
      }
      return;
    },
    onError: (_err, tag, context) => {
      const oldData =
        context?.oldData ??
        qc.getQueryData<InfiniteData<VoicemailQueryData> | VoicemailTableDataRow | undefined>([
          selectedLocationIds,
          ...queryKey,
        ]);

      if (oldData) {
        // if the request fails, revert the change with oldData without new tag
        qc.setQueryData<InfiniteData<VoicemailQueryData> | VoicemailTableDataRow | undefined>(
          [selectedLocationIds, ...queryKey],
          oldData
        );
      }

      onFailure?.({ tag, op: 'add', oldData });
    },
    onSuccess: (_, tag, context) => {
      onSuccess?.({ tag, op: 'add', newData: context?.newData });
    },
    //We could invalidate when the request has settled, but this will cause the table to re-render the skeleton loaders
    // onSettled: () => {
    //   qc.invalidateQueries([selectedLocationIds, ...queryKey]);
    // },
  });

  const { mutate: removeTag } = useMutation({
    mutationFn: async (tag: Tag) => {
      return VoicemailApi.untagVoicemail({ voicemailMessageId: voicemailId, tagIds: [tag.id] });
    },
    onMutate: async (tag: Tag) => {
      if (optimisticRemoveTag) {
        const oldData = qc.getQueryData<InfiniteData<VoicemailQueryData> | VoicemailTableDataRow | undefined>([
          selectedLocationIds,
          ...queryKey,
        ]);
        const newData = optimisticRemoveTag({ oldData, tag, voicemailId });
        if (newData) {
          qc.setQueryData<InfiniteData<VoicemailQueryData> | VoicemailTableDataRow | undefined>(
            [selectedLocationIds, ...queryKey],
            newData
          );
        }
        return { oldData, newData };
      }
      return;
    },
    onError: (_err, tag, context) => {
      const oldData =
        context?.oldData ??
        qc.getQueryData<InfiniteData<VoicemailQueryData> | VoicemailTableDataRow | undefined>([
          selectedLocationIds,
          ...queryKey,
        ]);

      if (oldData) {
        qc.setQueryData<InfiniteData<VoicemailQueryData> | VoicemailTableDataRow | undefined>(
          [selectedLocationIds, ...queryKey],
          oldData
        );
      }
      onFailure?.({ tag, op: 'remove', oldData });
    },
    onSuccess: (_, tag, context) => {
      onSuccess?.({ tag, op: 'remove', newData: context?.newData });
    },
    //We could invalidate when the request has settled, but this will cause the table to re-render the skeleton loaders
    // onSettled: () => {
    //   qc.invalidateQueries([selectedLocationIds, ...queryKey]);
    // },
  });

  return { addTag, removeTag };
};
